import type { MouseEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { SIZING_2 } from '../../theme';
import { Button } from '../Button';
import Icons from '../Icons';
import { Text } from '../Text';

type AlertProps = CommonProps & (WithoutIconProps | IconProps);

interface CommonProps {
  action?: {
    text: string;
    loading?: boolean;
  } & (
    | { href?: undefined; onClick: (e: MouseEvent) => void }
    | { href: string; onClick?: (e: MouseEvent) => void }
  );
  children: ReactNode;
  title?: string;
}

interface WithoutIconProps {
  type: 'info' | 'warning' | 'error' | 'smart';
  icon?: undefined;
}

interface IconProps {
  type?: 'default';
  icon?: ReactNode;
}

const BoldText = styled(Text)`
  font-weight: 600;
`;

const IconWrapper = styled.div`
  > * {
    width: ${props => props.theme.sizingIconXs};
    height: ${props => props.theme.sizingIconXs};
  }
`;

const Wrapper = styled.aside<{ $type: AlertProps['type'] }>`
  display: grid;
  gap: ${SIZING_2};
  align-items: center;
  max-width: 100%;
  text-align: left;
  grid-template-columns: auto 1fr auto;
  color: ${props => props.theme.colorTextBodyPrimary};
  min-height: ${props => props.theme.sizingAlertDefault};
  border-radius: ${props => props.theme.radiusAlert};
  padding: 0 ${SIZING_2};
  > div {
    padding: ${SIZING_2} 0;
  }
  ${props => {
    switch (props.$type) {
      case 'error':
        return css`
          background: ${props.theme.colorBackgroundAlertError};
          ${IconWrapper} {
            color: ${props.theme.colorIconsDanger};
          }
        `;
      case 'info':
        return css`
          background: ${props.theme.colorBackgroundAlertInfo};
          ${IconWrapper} {
            color: ${props.theme.colorIconsInfo};
          }
        `;
      case 'smart':
        return css`
          background: ${props.theme.colorBackgroundAlertSmart};
          ${IconWrapper} {
            color: ${props.theme.colorIconsSmart};
          }
        `;
      case 'warning':
        return css`
          background: ${props.theme.colorBackgroundAlertWarning};
          ${IconWrapper} {
            color: ${props.theme.colorIconsWarning};
          }
        `;
      default:
        return css`
          background: ${props.theme.colorBackgroundAlertDefault};
          ${IconWrapper} {
            color: ${props.theme.colorIconsDefault};
          }
        `;
    }
  }}
`;

export const Alert = ({ type, icon, action, children, title }: AlertProps) => {
  const { t } = useTranslation();
  if (type === 'error') {
    icon = <Icons.Error />;
  } else if (type === 'info') {
    icon = <Icons.CircleInfo />;
  } else if (type === 'warning') {
    icon = <Icons.Warning />;
  } else if (type === 'smart') {
    icon = <Icons.Sparkle aria-label={t('common_ai_suggestion')} />;
  }

  return (
    <Wrapper $type={type}>
      {icon && <IconWrapper>{icon}</IconWrapper>}
      <Text as="div" variant="body">
        {title && (
          <BoldText as="span" variant="body">
            {title}{' '}
          </BoldText>
        )}
        {children}
      </Text>
      {action && (
        <Button
          configuration="text"
          loading={action.loading}
          {...(action.href
            ? {
                href: action.href,
                onClick: action.onClick,
              }
            : {
                type: 'button',
                onClick: action.onClick || (() => {}), // eslint-disable-line @typescript-eslint/no-empty-function
              })}
        >
          {action.text}
        </Button>
      )}
    </Wrapper>
  );
};
